/**
 * CSAT 满意度评价 PC-SDK 动态注入
 * 仅在国际站/国内站外网接入，内网不接入；UAT/生产按域名区分
 */

import isIntranet from '@/utils/isIntranet'
import isInternational from '@/utils/isInternational'
import store from '@/store'
import configEnvMap from '@/env/international/config'

// 外网域名兜底配置（当 env 未正确解析时使用），域名与 oauth config 中 intranet:false 保持一致
const EXTRANET_CSAT = {
  prod: {
    csatCssUrl: 'https://print-web.s3.cn-north-1.jdcloud-oss.com/csat/jdl-csat.css',
    csatJsUrl: 'https://print-web.s3.cn-north-1.jdcloud-oss.com/csat/jdl-csat.min.js',
    csatLopDn: 'common.outer.csat.jdl.com',
  },
  uat: {
    csatCssUrl: 'https://print-web.s3.cn-north-1.jdcloud-oss.com/csat-uat/jdl-csat.css',
    csatJsUrl: 'https://print-web.s3.cn-north-1.jdcloud-oss.com/csat-uat/jdl-csat.min.js',
    csatLopDn: 'common.outer.csat.jdl.com',
  },
}

// prefix 国内外站统一：预发 uat-csat.jdl.com，正式 csat.jdl.com
function getCsatPrefix(isUat) {
  return isUat ? 'https://uat-csat.jdl.com' : 'https://csat.jdl.com'
}

// 与 src/env/international/us-config/index.js 一致：用 configEnvMap[location.hostname].env 判断环境
function getCsatConfig(env, hostname, configEnvMap) {
  const hostConfig = configEnvMap && configEnvMap[hostname]
  const isUat = hostConfig ? hostConfig.env === 'uat' : (env && env.buildEnv === 'uat')
  if (env && env.csatCssUrl && env.csatJsUrl) {
    return {
      csatCssUrl: env.csatCssUrl,
      csatJsUrl: env.csatJsUrl,
      csatPrefix: env.csatPrefix || getCsatPrefix(isUat),
      csatLopDn: env.csatLopDn || EXTRANET_CSAT.prod.csatLopDn,
    }
  }
  if (hostConfig && hostConfig.env === 'prod') {
    const prodConfig = Object.assign({}, EXTRANET_CSAT.prod, { csatPrefix: getCsatPrefix(false) })
    return prodConfig
  }
  if (hostConfig && hostConfig.env === 'uat') {
    const uatConfig = Object.assign({}, EXTRANET_CSAT.uat, { csatPrefix: getCsatPrefix(true) })
    return uatConfig
  }
  return null
}

// locale 与 CSAT Language 字段 KEY 的映射（zh-cn -> zh_CN, en-us -> en_US）
function getCsatLanguageKey(locale) {
  if (!locale) return 'zh_CN'
  const map = { 'zh-cn': 'zh_CN', 'en-us': 'en_US', ar_SA: 'ar_SA' }
  return map[locale] || locale.replace('-', '_')
}

/**
 * 语言变更时同步 CSAT：调用 setLanguage(string) 切换当前语言，传入参数为 Language 字段中的 KEY（如 zh_CN、en_US）
 */
export function setCsatLanguage(locale) {
  if (typeof window === 'undefined' || !window.__JD_CSAT || typeof window.__JD_CSAT.setLanguage !== 'function') return
  const languageKey = getCsatLanguageKey(locale)
  window.__JD_CSAT.setLanguage(languageKey)
}

function initCsatInstance(config) {
  if (typeof window === 'undefined' || typeof window.JD_CSAT !== 'function') return
  window.__JD_CSAT = null
  const options = {
    id: 'screenshot-evaluation-csat', // 使用弹窗容器的 ID
    mountMode: 'embedded', 
    prefix: config.csatPrefix,
    systemCode: isInternational() ? 'IOPEN' : 'OPEN', // 国内 OPEN，国际 IOPEN
    userType: 2,
    routerType: 'mixin',
    language: getCsatLanguageKey(config.locale),
    languages: {
      ar_SA: { writingMode: 'rl' },
    },
    headers: {
      pin: config.pin || Math.random().toString(36).substring(2, 15),
      'Lop-Dn': config.csatLopDn || 'common.outer.csat.jdl.com',
    },
  }
  return window.JD_CSAT(options).then(function (instance) {
    window.__JD_CSAT = instance
    if (window.__JD_CSAT && typeof window.__JD_CSAT.setMountOptions === 'function') {
      window.__JD_CSAT.setMountOptions('screenshot-evaluation-csat', 'embedded')
    }
  })
}

/** 国内 OPENQR，国际 IOPENQR，供文档展开时上报场景编码 */
export function getDefaultCsatScenario() {
  return isInternational() ? 'IOPENQR' : 'OPENQR'
}

/**
 * 向 CSAT 上报场景编码（文档展开等时机调用）
 */
export function dispatchCsatUrl(scenarioCode) {
  if (typeof window === 'undefined' || !scenarioCode) return
  if (window.__JD_CSAT && typeof window.__JD_CSAT.dispatchCode === 'function') {
    window.__JD_CSAT.dispatchCode(scenarioCode)
  }
}

/**
 * 设置 CSAT 扩展字段，将当前路由信息存入 extra1，用于标识用户正在访问的文档页面
 * extra1 格式示例：AccessGuideDetail|unitId=267|docId=53375
 * @param {import('vue-router').Route} route - Vue Router 当前路由对象（$route）
 */
export function setCsatExtendFields(route) {
  if (typeof window === 'undefined' || !window.__JD_CSAT || typeof window.__JD_CSAT.setExtendFields !== 'function') return
  if (!route) return
  const { name, params = {} } = route
  const parts = [name]
  if (params.unitId !== undefined) parts.push(`unitId=${params.unitId}`)
  if (params.docId !== undefined) parts.push(`docId=${params.docId}`)
  if (params.toolName !== undefined) parts.push(`toolName=${params.toolName}`)
  if (params.docCode !== undefined) parts.push(`docCode=${params.docCode}`)
  const extra1 = parts.join('|')
  console.log('extra1-->', extra1)
  window.__JD_CSAT.setExtendFields({ extra1 })
}

/**
 * 进入文档页时先 destroy 再 init，使调查问卷每次进入都重新展示
 * 每次重新走 init 流程，pin 用随机数，locale 从 store 取
 * 仅在匹配 access-guide / api-doc 的页面由路由 beforeEach 调用
 * @param {import('vue-router').Route} [route] - 当前路由对象，用于初始化后设置扩展字段
 * @returns {Promise<void>} init 完成后 resolve
 */
export function reinitCsatForPage(route) {
  if (typeof window === 'undefined') return Promise.resolve()
  if (window.__JD_CSAT && typeof window.__JD_CSAT.destroy === 'function') {
    window.__JD_CSAT.destroy()
  }
  window.__JD_CSAT = null
  const config = getCsatConfig(window.env, window.location.hostname, configEnvMap)
  if (!config || typeof window.JD_CSAT !== 'function') return Promise.resolve()
  const userState = store.state && store.state.user
  config.locale = (userState && userState.locale) || 'zh-cn'
  const p = initCsatInstance(config)
  if (p) {
    return Promise.resolve(p).then(() => {
      setCsatExtendFields(route)
    })
  }
  return Promise.resolve()
}

/**
 * 根据当前 env 注入满意度评价 PC-SDK
 */
export function injectCsat(env) {
  if (typeof window === 'undefined' || !document || !document.head) return
  if (isIntranet()) return

  const hostname = window.location.hostname
  const config = getCsatConfig(env, hostname, configEnvMap)
  if (!config) return

  const link = document.createElement('link')
  link.rel = 'stylesheet'
  link.href = config.csatCssUrl
  document.head.appendChild(link)

  const script = document.createElement('script')
  script.src = config.csatJsUrl
  script.onload = function () {
    const userState = store.state && store.state.user
    // pin 仅用随机数，与 state 区分；locale 仍从 store 取
    config.locale = (userState && userState.locale) || 'zh-cn'
    initCsatInstance(config)
  }
  document.head.appendChild(script)
}
